import React, { useContext, useState, useEffect } from "react";
import { AppContext } from "../../../../../provider/AppProvider";
import { ModuleContext, ImageContext, ProductContext } from "../../../provider";
import imageComp from "..";
import globalComp from "../../../../../global/components";
import BrowserStorage from "../../../../../config/core/BrowserStorage";
import {
  InstagramResponseData,
  InstagramPhotosResponse,
  InstagramPhotos,
  IGCarouselResponse,
} from "../../../../../Types";
import { fetchApi } from "../../../../../config/core/Api";
import { AxiosResponse } from "axios";
import { UploadFromUrlResponse } from "./FBImages";

interface IGImagesProps {}

function IGImages({}: IGImagesProps): JSX.Element {
  let t = window.INITIAL_DATA.TRANSLATIONS;

  const { instagramResponseData: ird, setInstagramResponseData } = useContext(
    AppContext
  );
  const { isFilteredImage, moduleImages } = useContext(ModuleContext);
  const { addPhotoWithUrl, instagramLogin, setSelectedUploadType } = useContext(
    ImageContext
  );
  const { productType } = useContext(ProductContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [media, setMedia] = useState<InstagramPhotosResponse | null>(null);
  const [formattedMedia, setFormattedMedia] = useState<InstagramPhotos[]>([]);

  const [mediaCarouselId, setMediaCarouselId] = useState<string | null>(null);
  const [mediaCarousel, setMediaCarousel] = useState<IGCarouselResponse | null>(
    null
  );

  const [username, setUsername] = useState<string | null>(null);

  const [paginationConfig, setPaginationConfig] = useState<any>({
    previous: null,
    next: null,
  });

  /**
   * The whole process is triggered by the carousel photo id.
   * This is id is set to null whn the user goes back from it.
   * setFormattedMedia is the same for both images and images within carousel.
   */
  useEffect(() => {
    if (!mediaCarouselId) {
      checkAuthData();
    }
  }, [mediaCarouselId]);

  useEffect(() => {
    if (media) {
      let tempMedia = [];
      media.data.map((elem) => {
        tempMedia.push({
          ...elem,
          isSelected: false,
        });
      });
      setFormattedMedia([...tempMedia]);
    }
  }, [media]);

  useEffect(() => {
    if (mediaCarouselId) {
      getMediaCarousel();
    }
  }, [mediaCarouselId]);

  useEffect(() => {
    if (mediaCarousel) {
      let tempMedia = [];
      mediaCarousel.data.map((elem) => {
        tempMedia.push({
          ...elem,
          isSelected: false,
        });
      });
      setFormattedMedia([...tempMedia]);
    }
  }, [mediaCarousel]);

  function checkAuthData() {
    if (BrowserStorage.getStorageItem("igAuthData")) {
      let igData: InstagramResponseData | null = JSON.parse(
        BrowserStorage.getStorageItem("igAuthData")
      ) as InstagramResponseData;
      if (igData) {
        setInstagramResponseData(igData);
        getUsername(igData);
        getMedia(igData);
      }
    }
  }
  window.instagramAuthenticationCheck = checkAuthData;

  /**
   * @method getUsername
   * @param igData
   * @returns void
   * @description Method to fetch user's info
   */
  function getUsername(igData: InstagramResponseData): void {
    setIsLoading(true);
    fetchApi({
      method: "GET",
      baseUrl: "https://graph.instagram.com",
      url: `/${igData.user_id}?fields=username&access_token=${igData.access_token}`,
    })
      .then((r: AxiosResponse<any>) => {
        if (r.status == 200) {
          setUsername(r.data.username);
        } else {
          igGeneralFailure();
        }
      })
      .catch((e) => {
        igGeneralFailure();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  /**
   * @method getMedia
   * @param igData
   * @returns void
   * @description Method to fetch user's media.
   */
  function getMedia(igData: InstagramResponseData): void {
    setIsLoading(true);
    fetchApi({
      method: "GET",
      baseUrl: "https://graph.instagram.com",
      url: `/${igData.user_id}/media?fields=caption,media_type,thumbnail_url,media_url&limit=12&access_token=${igData.access_token}`,
    })
      .then((r: AxiosResponse<InstagramPhotosResponse>) => {
        if (r.status == 200) {
          if (r.data.paging) {
            setMedia(r.data);
            setPaginationConfig({
              next: r.data.paging.next ? r.data.paging.next : null,
              previous: r.data.paging.previous ? r.data.paging.previous : null,
              editParentState: setMedia,
              editSelfState: setPaginationConfig,
            });
          }
        } else {
          igGeneralFailure();
        }
      })
      .catch((e) => {
        igGeneralFailure();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function getMediaCarousel() {
    if (BrowserStorage.getStorageItem("igAuthData")) {
      let igData: InstagramResponseData | null = JSON.parse(
        BrowserStorage.getStorageItem("igAuthData")
      );
      setIsLoading(true);
      fetchApi({
        method: "GET",
        baseUrl: "https://graph.instagram.com",
        url: `/${mediaCarouselId}/children?fields=id,media_type,media_url,thumbnail_url&access_token=${igData.access_token}`,
      })
        .then((r: AxiosResponse<IGCarouselResponse>) => {
          if (r.status == 200) {
            setMediaCarousel(r.data);
            // in instagram's carousel are allowed max 10 photos, no need for pagination
            setPaginationConfig({
              next: null,
              previous: null,
              editParentState: setMediaCarousel,
              editSelfState: setPaginationConfig,
            });
          } else {
            igGeneralFailure();
          }
        })
        .catch((e) => {
          igGeneralFailure();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function handlePhotoSelect(url: string) {
    if (
      moduleImages &&
      ((moduleImages.length == 1 && productType == "text-logo") ||
        (moduleImages.length == 3 && productType == "pop-art"))
    ) {
      window.globalToast({
        type: "warning",
        title: t.warning,
        description: t.dropzone_MaxFilesExceeded,
      });
    } else {
      setIsLoading(true);
      fetchApi({
        method: "POST",
        url: isFilteredImage
          ? "?action=upload_image_from_url&isFiltered=1"
          : "?action=upload_image_from_url",
        data: {
          sources: [url],
        },
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((r: AxiosResponse<UploadFromUrlResponse[]>) => {
          let tempMedia: any = JSON.parse(JSON.stringify(formattedMedia));
          tempMedia.map((elem) => {
            if (elem.media_url == url || elem.thumbnail_url == url) {
              elem.isSelected = !elem.isSelected;
              setFormattedMedia([...tempMedia]);
            }
          });
          addPhotoWithUrl(
            r.data[0],
            r.data[0].filtered_id,
            r.data[0].original_id
          );
        })
        .catch((e) => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function igGeneralFailure() {
    resetIgData();
    window.globalToast({
      type: "error",
      title: t.error,
      description: t.something_went_wrong_instagram,
    });
  }

  function resetIgData() {
    BrowserStorage.removeStorageItem("igAuthData");
    setInstagramResponseData(null);
    setMediaCarouselId(null);
    setMediaCarousel(null);
    setFormattedMedia([]);
  }

  return (
    <div className={"instagram-uploads"}>
      <div
        className={
          "position-relative d-flex justify-content-center align-items-center flex-wrap"
        }
        style={{ minHeight: 150 }}>
        {isLoading ? (
          <div className={"facebook-uploads__loader"}>
            <globalComp.Spinner size={30} color={"white"} />
          </div>
        ) : (
          <imageComp.SNAlbumMessage
            albumsDetails={formattedMedia}
            data={ird}
            type={"instagram"}
          />
        )}
        {username && (
          <div className={"facebook-uploads__wrap"}>
            <imageComp.SNHeader
              isBackActionVisible={!!mediaCarouselId}
              backAction={() => {
                setMediaCarouselId(null);
                setMediaCarousel(null);
                setFormattedMedia([]);
              }}
              logoutAction={() => {
                resetIgData();
                setSelectedUploadType("device");
                window.globalToast({
                  type: "success",
                  title: t.success,
                  description: t.success_logout_instagram,
                });
              }}
              userName={`${username}`}
            />

            {formattedMedia &&
              formattedMedia.length > 0 &&
              formattedMedia.map((media, index) => {
                if (media.media_type == "CAROUSEL_ALBUM") {
                  return (
                    <imageComp.SNAlbumsCell
                      key={`${media.id}-${index}`}
                      coverUrl={media.media_url}
                      albumName={media.caption}
                      onClick={() => {
                        setMediaCarouselId(media.id);
                      }}
                    />
                  );
                } else if (media.media_type == "IMAGE") {
                  return (
                    <imageComp.ImageCell
                      key={`${media.id}-${index}`}
                      imageUrl={media.media_url}
                      isChecked={media.isSelected}
                      onClick={() => {
                        handlePhotoSelect(media.media_url);
                      }}
                      removeIsCheckedTimeout={2000}
                    />
                  );
                } else if (media.media_type == "VIDEO") {
                  return (
                    <imageComp.ImageCell
                      key={`${media.id}-${index}`}
                      imageUrl={media.thumbnail_url}
                      isChecked={media.isSelected}
                      onClick={() => {
                        handlePhotoSelect(media.media_url);
                      }}
                      removeIsCheckedTimeout={2000}
                    />
                  );
                }
              })}

            <imageComp.ImagePagination
              paginationConfig={paginationConfig}
              setIsLoading={setIsLoading}
              errorFunc={igGeneralFailure}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default IGImages;
